/**
 * Date Parser Tool
 * 
 * Parses dates and outputs in various formats.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const DateParserTool: ITransformerTool = {
  id: 'date-parser',
  name: 'Date Parser',
  description: 'Parse dates and convert between formats',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['date', 'parse', 'format', 'convert', 'time'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.TIMESTAMP],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'outputFormat',
      type: 'select',
      label: 'Output Format',
      defaultValue: 'iso',
      options: ['iso', 'locale', 'utc', 'date-only', 'time-only', 'relative'],
      description: 'Format for the output',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const outputFormat = (config.outputFormat as string) || 'iso';
    const trimmed = input.trim();

    // Try to parse the date
    let date: Date;
    
    // Check if it's a Unix timestamp
    if (/^\d+$/.test(trimmed)) {
      let timestamp = parseInt(trimmed, 10);
      if (timestamp < 10000000000) {
        timestamp *= 1000;
      }
      date = new Date(timestamp);
    } else {
      date = new Date(trimmed);
    }

    if (isNaN(date.getTime())) {
      return `Unable to parse: "${trimmed}"`;
    }

    switch (outputFormat) {
      case 'iso':
        return date.toISOString();
      case 'locale':
        return date.toLocaleString();
      case 'utc':
        return date.toUTCString();
      case 'date-only':
        return date.toLocaleDateString();
      case 'time-only':
        return date.toLocaleTimeString();
      case 'relative':
        return getRelativeTime(date);
      default:
        return date.toISOString();
    }
  },
};

/**
 * Get relative time string (e.g., "5 minutes ago").
 */
function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  const isFuture = diffMs < 0;
  const abs = (n: number) => Math.abs(n);

  if (abs(diffSeconds) < 60) {
    return isFuture ? 'in a few seconds' : 'just now';
  } else if (abs(diffMinutes) < 60) {
    const m = abs(diffMinutes);
    return isFuture ? `in ${m} minute${m !== 1 ? 's' : ''}` : `${m} minute${m !== 1 ? 's' : ''} ago`;
  } else if (abs(diffHours) < 24) {
    const h = abs(diffHours);
    return isFuture ? `in ${h} hour${h !== 1 ? 's' : ''}` : `${h} hour${h !== 1 ? 's' : ''} ago`;
  } else {
    const d = abs(diffDays);
    return isFuture ? `in ${d} day${d !== 1 ? 's' : ''}` : `${d} day${d !== 1 ? 's' : ''} ago`;
  }
}

